/**
 * Verification Review Dashboard (Admin)
 * Admin interface to review and approve/reject verification applications
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Tabs,
  Tab,
  Grid,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Divider,
  LinearProgress
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Info,
  Visibility,
  Close,
  Email,
  Phone,
  Business,
  Schedule
} from '@mui/icons-material';
import api from '../../services/api';
import DialogCloseButton from '../common/DialogCloseButton';

const VerificationReviewDashboard = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [reviewDialog, setReviewDialog] = useState(false);
  const [infoDialog, setInfoDialog] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [reviewNotes, setReviewNotes] = useState('');
  const [requestedInfo, setRequestedInfo] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchApplications();
  }, [tabValue]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const status = tabValue === 0 ? 'pending' : 'under_review';
      const response = await api.get(`/admin/verification/applications?status=${status}`);
      const data = response.data;
      setApplications(data.applications || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (decision) => {
    try {
      await api.post(`/admin/verification/review/${selectedApp.id}`, {
        decision,
        notes: reviewNotes
      });

      setReviewDialog(false);
      setReviewNotes('');
      fetchApplications();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRequestInfo = async () => {
    try {
      await api.post(`/admin/verification/request-info/${selectedApp.id}`, { requestedInfo });

      setInfoDialog(false);
      setRequestedInfo('');
      fetchApplications();
    } catch (err) {
      setError(err.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'under_review': return 'info';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const calculateProfileScore = (app) => {
    // Simplified score calculation for display
    return 85; // TODO: Calculate from actual profile data
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Verification Applications
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab label="Pending" />
        <Tab label="Under Review" />
      </Tabs>

      {loading ? (
        <LinearProgress />
      ) : applications.length === 0 ? (
        <Alert severity="info">No applications to review</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Applicant</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Applied</TableCell>
                <TableCell>Profile Score</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {applications.map((app) => (
                <TableRow key={app.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar>{app.first_name?.[0]}{app.last_name?.[0]}</Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {app.first_name} {app.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {app.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={app.role} size="small" />
                  </TableCell>
                  <TableCell>
                    {new Date(app.applied_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={`${calculateProfileScore(app)}%`} 
                      color={calculateProfileScore(app) >= 80 ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={app.status} 
                      color={getStatusColor(app.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      startIcon={<Visibility />}
                      onClick={() => {
                        setSelectedApp(app);
                        setReviewDialog(true);
                      }}
                    >
                      Review
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Review Dialog */}
      <Dialog
        open={reviewDialog}
        onClose={() => setReviewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={() => setReviewDialog(false)} />
        {selectedApp && (
          <>
            <DialogTitle>
              Review Application
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                {/* Applicant Info */}
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Applicant Information
                      </Typography>
                      <List dense>
                        <ListItem>
                          <Email sx={{ mr: 1 }} fontSize="small" />
                          <ListItemText primary={selectedApp.email} />
                        </ListItem>
                        {selectedApp.phone_number && (
                          <ListItem>
                            <Phone sx={{ mr: 1 }} fontSize="small" />
                            <ListItemText primary={selectedApp.phone_number} />
                          </ListItem>
                        )}
                        {selectedApp.company_name && (
                          <ListItem>
                            <Business sx={{ mr: 1 }} fontSize="small" />
                            <ListItemText primary={selectedApp.company_name} />
                          </ListItem>
                        )}
                        <ListItem>
                          <Schedule sx={{ mr: 1 }} fontSize="small" />
                          <ListItemText 
                            primary={`Member since: ${new Date(selectedApp.user_created_at).toLocaleDateString()}`} 
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Profile Score */}
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Profile Completion
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={calculateProfileScore(selectedApp)}
                            color={calculateProfileScore(selectedApp) >= 80 ? 'success' : 'warning'}
                            sx={{ height: 8, borderRadius: 1 }}
                          />
                        </Box>
                        <Typography variant="h6">
                          {calculateProfileScore(selectedApp)}%
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Application Data */}
                {selectedApp.application_data && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Application Details
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {typeof selectedApp.application_data === 'string' 
                            ? selectedApp.application_data 
                            : JSON.stringify(JSON.parse(selectedApp.application_data), null, 2)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Review Notes */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Review Notes"
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="Add notes about your decision..."
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Info />}
                onClick={() => {
                  setReviewDialog(false);
                  setInfoDialog(true);
                }}
              >
                Request More Info
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="outlined"
                color="error"
                startIcon={<Cancel />}
                onClick={() => handleReview('reject')}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                onClick={() => handleReview('approve')}
              >
                Approve
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Request Info Dialog */}
      <Dialog
        open={infoDialog}
        onClose={() => setInfoDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogCloseButton onClick={() => setInfoDialog(false)} />
        <DialogTitle>Request Additional Information</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="What information do you need?"
            value={requestedInfo}
            onChange={(e) => setRequestedInfo(e.target.value)}
            placeholder="Specify what documents or information the applicant should provide..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleRequestInfo}
            disabled={!requestedInfo}
          >
            Send Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VerificationReviewDashboard;








